/**
 * The four user-facing settlement operations on a Solana pool, mirroring
 * the EVM operation set: private transfer, withdrawal, batch withdrawal and
 * migration of a retired tree. Each one selects and authenticates notes,
 * builds the output notes, and hands a plan to `proveAndRelaySolanaSettlement`.
 */
import { computeSolanaTokenHash } from "@stealthos/sdk";
import { solanaRecipientInboxKey } from "./recipient";
import type { JoinSplitNote, StoredJoinSplitNote } from "../../joinsplit/types";
import { findSolanaToken, type SolanaNetworkConfig } from "../../config/solana";
import { flatFieldHex } from "../notes";
import { selectForAmount } from "../spendSelection";
import { fail } from "./common";
import type { SolanaJournalDelivery } from "./journal";
import { solanaJournalReservations } from "./journal";
import {
  assertSolanaFlatNote,
  createSolanaFlatNote,
  isSolanaFlatNote,
  solanaNoteMint,
  solanaTokenProgramForNote,
} from "./notes";
import {
  proveAndRelaySolanaSettlement,
  type SolanaSettlementContext,
} from "./settle";
import { readSolanaPoolConfig } from "./sync";
import {
  assertSolanaWithdrawalQuote,
  type SolanaWithdrawalQuote,
} from "./fees";

export interface SolanaOperationResult {
  success: boolean;
  signature?: string;
  slot?: number;
  spentNotes: JoinSplitNote[];
  outputNotes: JoinSplitNote[];
  changeNote?: JoinSplitNote;
  journalId?: string;
  replayed?: boolean;
  error?: string;
}

export interface SolanaOperationBase {
  ctx: SolanaSettlementContext;
  notes: StoredJoinSplitNote[];
  mint: string | null;
}

export interface SolanaTransferParams extends SolanaOperationBase {
  recipientMpk: bigint;
  recipientInboxPublicKeyHex?: string;
  recipientPrivateHistory?: JoinSplitNote["privateHistory"];
  changePrivateHistory?: JoinSplitNote["privateHistory"];
  amount: bigint;
}

export interface SolanaWithdrawParams extends SolanaOperationBase {
  /** Base58 owner that receives `amount - fee`. */
  recipientOwner: string;
  /** Gross amount leaving the private balance. */
  amount: bigint;
  /** Exact quote the user reviewed before proving. No fixed fallback. */
  feeQuote?: SolanaWithdrawalQuote;
  changePrivateHistory?: JoinSplitNote["privateHistory"];
}

export interface SolanaBatchWithdrawParams extends SolanaOperationBase {
  /** Recipients receive their amounts exactly; the fee is charged on top. */
  items: Array<{ owner: string; amount: bigint }>;
  feeQuote?: SolanaWithdrawalQuote;
  changePrivateHistory?: JoinSplitNote["privateHistory"];
}

export interface SolanaMigrateParams extends SolanaOperationBase {
  sourceTreeIndex: number;
  /** Omit to migrate every spendable note of the token in that tree. */
  commitments?: string[];
}

const SOLANA_MAX_INPUT_NOTES = 32;
const SOLANA_MAX_BATCH_RECIPIENTS = 32;

function solanaCandidateNotes(
  notes: StoredJoinSplitNote[],
  network: SolanaNetworkConfig,
  mint: string | null,
): StoredJoinSplitNote[] {
  return notes.filter((stored) => {
    const note = stored.note;
    if (stored.spent || !isSolanaFlatNote(note, network)) return false;
    if (note.treeIndex === undefined || note.leafIndex < 0) return false;
    try {
      return solanaNoteMint(note) === mint;
    } catch {
      return false;
    }
  });
}

/** Authenticate candidates and keep one record per commitment. */
async function solanaAuthenticatedCandidates(
  ctx: SolanaSettlementContext,
  candidates: StoredJoinSplitNote[],
  mint: string | null,
): Promise<StoredJoinSplitNote[]> {
  const authenticated = new Map<string, StoredJoinSplitNote>();
  for (const stored of candidates) {
    try {
      await assertSolanaFlatNote({
        note: stored.note,
        network: ctx.network,
        mint,
        expectedTokenHash: flatFieldHex(stored.note.tokenHash),
        expectedMpk: ctx.spendingKeys.mpk,
      });
      const key = stored.note.commitment.toLowerCase();
      if (!authenticated.has(key)) authenticated.set(key, stored);
    } catch {
      // Untrusted mailbox duplicates must not shadow an authentic record.
    }
  }
  return [...authenticated.values()];
}

function failure(error: unknown): SolanaOperationResult {
  return {
    success: false,
    spentNotes: [],
    outputNotes: [],
    error: error instanceof Error ? error.message : String(error),
  };
}

export async function currentTreeContext(
  ctx: SolanaSettlementContext,
  mint: string | null,
): Promise<{
  currentTreeIndex: number;
  currentTokenHash: string;
  feeRecipient: string;
}> {
  const { poolConfig, addresses } = await readSolanaPoolConfig(
    ctx.rpc,
    ctx.network,
  );
  return {
    currentTreeIndex: poolConfig.currentTreeIndex,
    feeRecipient: poolConfig.feeRecipient,
    currentTokenHash: computeSolanaTokenHash({
      cluster: ctx.network.cluster,
      programId: addresses.programId,
      poolConfig: addresses.poolConfig,
      treeIndex: poolConfig.currentTreeIndex,
      mint,
    }),
  };
}

export async function spendableInTree(
  params: SolanaOperationBase,
  treeIndex: number,
): Promise<StoredJoinSplitNote[]> {
  const authenticated = await solanaAuthenticatedCandidates(
    params.ctx,
    solanaCandidateNotes(params.notes, params.ctx.network, params.mint),
    params.mint,
  );
  const reservations = await solanaJournalReservations(params.ctx.journalScope);
  const inTree = authenticated.filter(
    ({ note }) => note.treeIndex === treeIndex,
  );
  const available = inTree.filter(
    ({ note }) => !reservations.commitments.has(note.commitment.toLowerCase()),
  );
  if (inTree.length > 0 && available.length === 0) {
    fail(
      "these private notes are already in flight; sync to reconcile the pending settlement",
    );
  }
  return available;
}

export function selfDelivery(
  note: JoinSplitNote,
  selfMpk: bigint,
): SolanaJournalDelivery {
  return {
    commitment: note.commitment,
    recipientMpkHex: flatFieldHex(selfMpk),
    keepLocal: true,
  };
}

export async function executeSolanaTransfer(
  params: SolanaTransferParams,
): Promise<SolanaOperationResult> {
  const { ctx } = params;
  try {
    if (params.amount <= 0n) fail("amount must be positive");
    if (
      (params.recipientMpk !== ctx.spendingKeys.mpk ||
        params.recipientInboxPublicKeyHex) &&
      !solanaRecipientInboxKey(params.recipientInboxPublicKeyHex)
    ) {
      fail(
        "a private recipient must provide an inbox public key for note delivery",
      );
    }
    const { currentTreeIndex, currentTokenHash } = await currentTreeContext(
      ctx,
      params.mint,
    );
    const available = await spendableInTree(params, currentTreeIndex);
    const selected = selectForAmount(available, params.amount);
    const total = selected.reduce((sum, { note }) => sum + note.value, 0n);
    const tokenProgram = solanaTokenProgramForNote(selected[0].note);
    if (
      flatFieldHex(selected[0].note.tokenHash) !==
      currentTokenHash.toLowerCase()
    ) {
      fail("selected notes are not in the pool's current token domain");
    }
    const recipientNote: JoinSplitNote = {
      ...(await createSolanaFlatNote({
        recipientMpk: params.recipientMpk,
        network: ctx.network,
        treeIndex: currentTreeIndex,
        mint: params.mint,
        tokenProgram,
        tokenHash: currentTokenHash,
        value: params.amount,
      })),
      ...(params.recipientPrivateHistory
        ? { privateHistory: params.recipientPrivateHistory }
        : {}),
    };
    const outputs = [recipientNote];
    if (total > params.amount) {
      outputs.push({
        ...(await createSolanaFlatNote({
          recipientMpk: ctx.spendingKeys.mpk,
          network: ctx.network,
          treeIndex: currentTreeIndex,
          mint: params.mint,
          tokenProgram,
          tokenHash: currentTokenHash,
          value: total - params.amount,
        })),
        ...(params.changePrivateHistory
          ? { privateHistory: params.changePrivateHistory }
          : {}),
      });
    }
    const isSelf = params.recipientMpk === ctx.spendingKeys.mpk;
    ctx.onLog("Preparing a depth-24 private transfer witness", "info");
    const settled = await proveAndRelaySolanaSettlement(ctx, {
      kind: "joinsplit",
      mint: params.mint,
      selected,
      outputs,
      deliveries: outputs.map((note, index) =>
        index === 0
          ? {
              commitment: note.commitment,
              recipientMpkHex: flatFieldHex(params.recipientMpk),
              recipientInboxPublicKeyHex: params.recipientInboxPublicKeyHex,
              keepLocal: isSelf,
            }
          : selfDelivery(note, ctx.spendingKeys.mpk),
      ),
    });
    return {
      success: true,
      signature: settled.signature,
      slot: settled.slot,
      spentNotes: selected.map(({ note }) => note),
      outputNotes: settled.outputs,
      changeNote: settled.outputs[1],
      journalId: settled.journalId,
      replayed: settled.replayed,
    };
  } catch (error) {
    return failure(error);
  }
}

function quotedSelection(
  available: StoredJoinSplitNote[],
  approved: SolanaWithdrawalQuote,
) {
  const commitments = approved.selectedCommitments;
  if (
    !commitments.length ||
    commitments.length > 32 ||
    new Set(commitments).size !== commitments.length
  )
    fail("Invalid quoted note selection");
  return commitments.map((commitment) => {
    const note = available.find(
      (stored) => stored.note.commitment === commitment,
    );
    if (!note)
      fail(
        "Quoted notes are no longer available. Refresh and review the fee again.",
      );
    return note;
  });
}

export async function executeSolanaWithdraw(
  params: SolanaWithdrawParams,
): Promise<SolanaOperationResult> {
  const { ctx } = params;
  try {
    if (params.amount <= 0n) fail("amount must be positive");
    if (!params.feeQuote)
      fail("Review a fresh Solana fee quote before withdrawing");
    const { currentTreeIndex, currentTokenHash, feeRecipient } =
      await currentTreeContext(ctx, params.mint);
    const available = await spendableInTree(params, currentTreeIndex);
    const selected = quotedSelection(available, params.feeQuote);
    const total = selected.reduce((sum, { note }) => sum + note.value, 0n);
    const fee = assertSolanaWithdrawalQuote(params.feeQuote, {
      network: ctx.network,
      mint: params.mint,
      kind: "withdraw",
      treeIndex: currentTreeIndex,
      amount: params.amount,
      recipients: [params.recipientOwner],
      inputTotal: total,
      selectedCommitments: selected.map(({ note }) => note.commitment),
    });
    if (params.feeQuote.quote.feeRecipient !== feeRecipient)
      fail("Pool fee recipient changed; review a new quote");
    const outputs: JoinSplitNote[] = [];
    if (total > params.amount) {
      outputs.push({
        ...(await createSolanaFlatNote({
          recipientMpk: ctx.spendingKeys.mpk,
          network: ctx.network,
          treeIndex: currentTreeIndex,
          mint: params.mint,
          tokenProgram: solanaTokenProgramForNote(selected[0].note),
          tokenHash: currentTokenHash,
          value: total - params.amount,
        })),
        ...(params.changePrivateHistory
          ? { privateHistory: params.changePrivateHistory }
          : {}),
      });
    }
    ctx.onLog("Preparing a depth-24 private withdrawal witness", "info");
    const settled = await proveAndRelaySolanaSettlement(ctx, {
      kind: "withdraw",
      mint: params.mint,
      selected,
      outputs,
      recipientOwner: params.recipientOwner,
      withdrawAmount: params.amount,
      fee,
      deliveries: outputs.map((note) =>
        selfDelivery(note, ctx.spendingKeys.mpk),
      ),
    });
    return {
      success: true,
      signature: settled.signature,
      slot: settled.slot,
      spentNotes: selected.map(({ note }) => note),
      outputNotes: settled.outputs,
      changeNote: settled.outputs[0],
      journalId: settled.journalId,
      replayed: settled.replayed,
    };
  } catch (error) {
    return failure(error);
  }
}

export async function executeSolanaBatchWithdraw(
  params: SolanaBatchWithdrawParams,
): Promise<SolanaOperationResult> {
  const { ctx } = params;
  try {
    if (
      params.items.length < 2 ||
      params.items.length > SOLANA_MAX_BATCH_RECIPIENTS
    ) {
      fail(`a batch pays 2..${SOLANA_MAX_BATCH_RECIPIENTS} recipients`);
    }
    if (params.items.some((item) => item.amount <= 0n)) {
      fail("every batch amount must be positive");
    }
    const total = params.items.reduce((sum, item) => sum + item.amount, 0n);
    if (!params.feeQuote)
      fail("Review a fresh Solana fee quote before withdrawing");
    const { currentTreeIndex, currentTokenHash, feeRecipient } =
      await currentTreeContext(ctx, params.mint);
    const available = await spendableInTree(params, currentTreeIndex);
    const selected = quotedSelection(available, params.feeQuote);
    const inputTotal = selected.reduce((sum, { note }) => sum + note.value, 0n);
    const fee = assertSolanaWithdrawalQuote(params.feeQuote, {
      network: ctx.network,
      mint: params.mint,
      kind: "withdraw_batch",
      treeIndex: currentTreeIndex,
      amount: total,
      recipients: params.items.map((item) => item.owner),
      inputTotal,
      selectedCommitments: selected.map(({ note }) => note.commitment),
    });
    if (params.feeQuote.quote.feeRecipient !== feeRecipient)
      fail("Pool fee recipient changed; review a new quote");
    const gross = total + fee;
    const outputs: JoinSplitNote[] = [];
    if (inputTotal > gross) {
      outputs.push({
        ...(await createSolanaFlatNote({
          recipientMpk: ctx.spendingKeys.mpk,
          network: ctx.network,
          treeIndex: currentTreeIndex,
          mint: params.mint,
          tokenProgram: solanaTokenProgramForNote(selected[0].note),
          tokenHash: currentTokenHash,
          value: inputTotal - gross,
        })),
        ...(params.changePrivateHistory
          ? { privateHistory: params.changePrivateHistory }
          : {}),
      });
    }
    ctx.onLog(
      `Preparing a private batch withdrawal to ${params.items.length} recipients`,
      "info",
    );
    const settled = await proveAndRelaySolanaSettlement(ctx, {
      kind: "withdraw_batch",
      mint: params.mint,
      selected,
      outputs,
      batch: {
        owners: params.items.map((item) => item.owner),
        amounts: params.items.map((item) => item.amount),
      },
      withdrawAmount: gross,
      fee,
      deliveries: outputs.map((note) =>
        selfDelivery(note, ctx.spendingKeys.mpk),
      ),
    });
    return {
      success: true,
      signature: settled.signature,
      slot: settled.slot,
      spentNotes: selected.map(({ note }) => note),
      outputNotes: settled.outputs,
      changeNote: settled.outputs[0],
      journalId: settled.journalId,
      replayed: settled.replayed,
    };
  } catch (error) {
    return failure(error);
  }
}

export async function executeSolanaMigration(
  params: SolanaMigrateParams,
): Promise<SolanaOperationResult> {
  const { ctx } = params;
  try {
    const { currentTreeIndex, currentTokenHash } = await currentTreeContext(
      ctx,
      params.mint,
    );
    if (params.sourceTreeIndex >= currentTreeIndex) {
      fail("only a retired tree can be migrated");
    }
    let candidates = await spendableInTree(params, params.sourceTreeIndex);
    if (params.commitments) {
      const wanted = new Set(
        params.commitments.map((value) => value.toLowerCase()),
      );
      candidates = candidates.filter(({ note }) =>
        wanted.has(note.commitment.toLowerCase()),
      );
    }
    if (candidates.length === 0) fail("no spendable notes in that tree");
    const selected = candidates
      .sort((left, right) =>
        left.note.value === right.note.value
          ? 0
          : left.note.value > right.note.value
            ? -1
            : 1,
      )
      .slice(0, SOLANA_MAX_INPUT_NOTES)
      .sort((left, right) => left.note.leafIndex - right.note.leafIndex);
    const total = selected.reduce((sum, { note }) => sum + note.value, 0n);
    const output = await createSolanaFlatNote({
      recipientMpk: ctx.spendingKeys.mpk,
      network: ctx.network,
      treeIndex: currentTreeIndex,
      mint: params.mint,
      tokenProgram: solanaTokenProgramForNote(selected[0].note),
      tokenHash: currentTokenHash,
      value: total,
    });
    ctx.onLog(
      `Migrating ${selected.length} note${selected.length === 1 ? "" : "s"} from tree ${params.sourceTreeIndex} to tree ${currentTreeIndex}`,
      "info",
    );
    const settled = await proveAndRelaySolanaSettlement(ctx, {
      kind: "migrate",
      mint: params.mint,
      selected,
      outputs: [output],
      deliveries: [selfDelivery(output, ctx.spendingKeys.mpk)],
    });
    return {
      success: true,
      signature: settled.signature,
      slot: settled.slot,
      spentNotes: selected.map(({ note }) => note),
      outputNotes: settled.outputs,
      journalId: settled.journalId,
      replayed: settled.replayed,
    };
  } catch (error) {
    return failure(error);
  }
}
